import { useEffect, useRef, useState } from 'react'
import {
  calculateLeftPadding,
  iterateFieldSchema,
} from 'routes/Integrations/helpers/schemas'
import { FieldSchemaRowType } from 'routes/Integrations/helpers/types'
import { IntegrationLoadMoreButton } from '../IntegrationLoadMoreButton'
import clsx from 'clsx'
import classes from './IntegrationSchema.module.scss'

const INITIAL_FIELDS_COUNT = 5

export const IntegrationSchema = ({
  schemaProps,
  dataName,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  schemaProps: Record<string, any>
  dataName: string
}) => {
  const [showMore, setShowMore] = useState(false)
  const elementToScroll = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setShowMore(false)
  }, [dataName])

  const toggleShowMoreBtn = () => setShowMore(!showMore)

  const newFieldSchema: Array<FieldSchemaRowType> =
    iterateFieldSchema(schemaProps)

  const hasDescriptionsForAllFields = newFieldSchema.some((i) => i.description)

  const isMoreFieldsAvailable = newFieldSchema.length > INITIAL_FIELDS_COUNT

  const numberOfAdditionalFields = newFieldSchema.length - INITIAL_FIELDS_COUNT

  const visibleFieldsSchema = showMore
    ? newFieldSchema
    : newFieldSchema.slice(0, INITIAL_FIELDS_COUNT)

  return (
    <div className={classes.table_border} ref={elementToScroll}>
      <div className={classes.table_wrapper}>
        <FieldSchemaTable
          showDescriptionColumn={hasDescriptionsForAllFields}
          fieldSchema={visibleFieldsSchema}
        />
      </div>

      {isMoreFieldsAvailable && (
        <IntegrationLoadMoreButton
          toggleButton={toggleShowMoreBtn}
          isActive={showMore}
          scrollAnchor={elementToScroll.current as HTMLDivElement}
          additionalItemsAmount={numberOfAdditionalFields}
          className={classes.button}
        />
      )}
    </div>
  )
}

function FieldSchemaTable({
  showDescriptionColumn,
  fieldSchema,
}: {
  showDescriptionColumn: boolean
  fieldSchema: Array<FieldSchemaRowType>
}) {
  return (
    <table className={classes.table}>
      <thead>
        <tr className={classes.table_row}>
          <th className={clsx(classes.table_head, classes.table_head__title)}>
            Field Name
          </th>
          <th className={classes.table_head}>API Key</th>
          <th className={clsx(classes.table_head, classes.table_head__type)}>
            Type
          </th>
          {showDescriptionColumn && (
            <th className={classes.table_head}>Description</th>
          )}
        </tr>
      </thead>
      <tbody>
        {fieldSchema.map((item, idx) => (
          <FieldSchemaTableRow key={idx} item={item} />
        ))}
      </tbody>
    </table>
  )
}

function FieldSchemaTableRow({ item }: { item: FieldSchemaRowType }) {
  const { title, apiKey, type, description, parentType, iterator } = item

  return (
    <tr className={classes.table_row}>
      <td
        className={classes.table_cell}
        style={calculateLeftPadding(iterator as number)}
      >
        {title}
      </td>
      <ApiKeyTableCell
        apiKey={apiKey}
        parentType={parentType}
        iterator={iterator as number}
      />
      <TypeTableCell type={type} />
      <td className={clsx(classes.table_cell)}>{description}</td>
    </tr>
  )
}

function ApiKeyTableCell({
  apiKey,
  parentType,
  iterator,
}: {
  apiKey: string[]
  parentType: string | undefined
  iterator: number
}) {
  const objectKeysString = apiKey.slice(0, -1).join('.')
  const lastKey = apiKey[apiKey.length - 1]

  return (
    <td
      className={clsx(classes.table_cell, classes.table_cell__fira)}
      style={calculateLeftPadding(iterator as number)}
    >
      {!parentType ? (
        <span>{apiKey}</span>
      ) : (
        <>
          {parentType === 'array' ? (
            <>
              <span
                className={clsx(classes.table_text, classes.table_text__grey)}
              >
                item.
              </span>
              <span>{lastKey}</span>
            </>
          ) : (
            <>
              <span
                className={clsx(classes.table_text, classes.table_text__light)}
              >
                {objectKeysString}.
              </span>
              <span>{lastKey}</span>
            </>
          )}
        </>
      )}
    </td>
  )
}

function TypeTableCell({ type }: { type: string }) {
  return (
    <td className={clsx(classes.table_cell, classes.table_cell__fira)}>
      {type === 'items' ? (
        <>
          <span>array of </span>
          <span className={(classes.table_text, classes.table_text__grey)}>
            {type}
          </span>
        </>
      ) : (
        type
      )}
    </td>
  )
}
